import type { Ref } from 'vue';
import type { VForm } from 'vuetify/components';
import type { SaveDraftMode } from '@/components/dialogs/SaveJobDraftDialog.vue';
import type JobBookingErrorPanel from '@/components/panels/JobBookingErrorPanel.vue';
import type { AfterBook } from '@/composables/jobBookings/useJobBookingSubmit';
import type { useDialogStore } from '@/stores/useDialogStore';
import type { BookingStop } from '@/models/advancedJobBooking';

import { nextTick } from 'vue';

import { scrollToFirstError } from '@/helpers/form';
import { showErrorNotification } from '@/helpers/notifiers';
import { hasDuplicateConsecutiveStops } from '@/helpers/stopsTimeline';

export interface JobBookingBookFlowOptions {
  form: Ref<InstanceType<typeof VForm> | null>;
  errorPanel: Ref<InstanceType<typeof JobBookingErrorPanel> | null>;
  stops: Ref<BookingStop[]>;
  submit: (after?: AfterBook) => Promise<boolean>;
  reset: () => void;
  saveDraft: (name: string, mode?: SaveDraftMode) => Promise<void>;
  draftId: Ref<number | null>;
  draftName: Ref<string>;
  dialogStore: ReturnType<typeof useDialogStore>;
  beforeProceed?: () => Promise<boolean>;
}

export interface JobBookingBookFlowComposable {
  handleBook: () => Promise<void>;
  handleBookMenuSelect: (value: string) => void;
  handleClearForm: () => void;
  handleSaveDraft: () => void;
}

export default function useJobBookingBookFlow(
  options: JobBookingBookFlowOptions,
): JobBookingBookFlowComposable {
  const {
    form,
    errorPanel,
    stops,
    submit,
    reset,
    saveDraft,
    draftId,
    draftName,
    dialogStore,
    beforeProceed,
  } = options;

  async function proceedBooking(after: AfterBook): Promise<void> {
    const booked = await submit(after);
    await nextTick();
    if (!booked) {
      errorPanel.value?.$el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    if (after === 'reset') {
      form.value?.resetValidation();
    }
  }

  async function bookWith(after: AfterBook): Promise<void> {
    if (!form.value) return;
    const { valid, errors } = await form.value.validate();
    if (!valid) {
      scrollToFirstError(errors);
      return;
    }
    if (hasDuplicateConsecutiveStops(stops.value)) {
      showErrorNotification({
        title: 'Consecutive stops have the same address',
        text: 'Please change one of them before booking.',
      });
      return;
    }
    if (beforeProceed && !(await beforeProceed())) return;
    await proceedBooking(after);
  }

  async function handleBook(): Promise<void> {
    await bookWith('navigate');
  }

  function handleBookMenuSelect(value: string): void {
    if (value === 'duplicate') {
      void bookWith('keep');
    } else if (value === 'createNew') {
      void bookWith('reset');
    }
  }

  function handleClearForm(): void {
    reset();
    form.value?.resetValidation();
  }

  function handleSaveDraft(): void {
    dialogStore.open('saveJobDraft', {
      confirm: saveDraft,
      initialName: draftName.value,
      isEditingDraft: draftId.value !== null,
    });
  }

  return { handleBook, handleBookMenuSelect, handleClearForm, handleSaveDraft };
}
